import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ArrowUp, ArrowDown, Plus, Trash2, ArrowRight, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { cn } from "@/lib/utils";
import {
  AVATAR_BG, CARD, PILL, THEAD, TR,
  inputCls, btnPrimary, btnGhost, iconBtn,
} from "./shared";

// Chaque niveau de priorité, décrit comme une règle : nom, condition évaluée, cible sonnée.
const PRIORITY_META = {
  responsable: {
    name: "Commercial responsable",
    condition: "Le numéro correspond à une entreprise déjà attribuée",
    target: "Commercial propriétaire",
  },
  remplacement: {
    name: "Commercial de remplacement",
    condition: "Le responsable est absent, occupé ou désactivé",
    target: "Remplaçant désigné",
  },
  autre_commercial: {
    name: "Autre commercial disponible",
    condition: "Groupes de secteur, puis round-robin des commerciaux disponibles",
    target: "Groupe du secteur",
  },
  manager: {
    name: "Manager",
    condition: "Aucun commercial n'est joignable",
    target: "Manager de routage",
  },
  secours: {
    name: "Numéros de secours",
    condition: "Aucun poste interne n'a décroché",
    target: "Numéros de secours",
  },
  messagerie: {
    name: "Messagerie",
    condition: "Dernier recours : la cascade est épuisée",
    target: "Messagerie",
  },
};

const BEHAVIORS = [["rejet", "Rejet automatique"], ["messagerie", "Messagerie"], ["numero_special", "Numéro spécial"]];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const LABEL = "block text-xs text-slate-500";

function Section({ title, subtitle, actions, children, className }) {
  return (
    <div className={cn(CARD, className)}>
      {(title || actions) && (
        <div className="px-5 py-3.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[13.5px] font-medium text-slate-800">{title}</div>
            {subtitle && <div className="text-xs text-slate-400 mt-0.5 text-pretty">{subtitle}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export default function RoutingSettings() {
  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/telephony/settings"),
      api.get("/telephony/team"),
      api.get("/telephony/groups"),
    ]).then(([s, t, g]) => { setSettings(s.data); setTeam(t.data); setGroups(g.data); })
      .catch(() => toast.error("Erreur de chargement"));
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const move = (i, dir) => {
    const order = [...settings.priority_order];
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    set("priority_order", order);
  };

  const save = async () => {
    try {
      const { data } = await api.put("/telephony/settings", settings);
      setSettings(data);
      toast.success("Paramètres de routage enregistrés");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveGroup = async () => {
    if (!groupForm.name?.trim()) return toast.error("Nom du groupe requis");
    const payload = {
      name: groupForm.name,
      member_ids: groupForm.member_ids || [],
      naf_prefixes: (groupForm.naf_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      departements: (groupForm.dept_text || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (groupForm.id) await api.put(`/telephony/groups/${groupForm.id}`, payload);
      else await api.post("/telephony/groups", payload);
      const { data } = await api.get("/telephony/groups");
      setGroups(data); setGroupForm(null);
      toast.success("Groupe enregistré");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteGroup = async (g) => {
    await api.delete(`/telephony/groups/${g.id}`);
    setGroups((gs) => gs.filter((x) => x.id !== g.id));
    toast.success("Groupe supprimé");
  };

  if (!settings) return <Loader />;
  const commercials = team.filter((u) => u.role === "commercial");
  const managers = team.filter((u) => u.role === "manager");
  const bh = settings.business_hours || {};
  const nameOf = (id) => team.find((u) => u.id === id)?.name || "?";
  const editGroup = (g) => setGroupForm({ ...g, naf_text: (g.naf_prefixes || []).join(", "), dept_text: (g.departements || []).join(", ") });

  return (
    <div className="flex flex-col gap-4" data-testid="routing-settings">
      {/* Règles de routage — l'ordre de priorité, évalué de haut en bas */}
      <Section
        title="Règles de routage"
        subtitle="Évaluées de haut en bas, la première correspondance gagne."
        className="overflow-hidden"
      >
        <div className={cn("hidden lg:grid px-5 py-2.5", THEAD)}
          style={{ gridTemplateColumns: "34px minmax(180px,1.4fr) minmax(160px,1.2fr) 96px 96px" }}>
          <div>#</div>
          <div>Règle</div>
          <div>Cible</div>
          <div className="text-right">Sonnerie</div>
          <div className="text-right">Ordre</div>
        </div>
        {settings.priority_order.map((p, i) => {
          const meta = PRIORITY_META[p] || { name: p, condition: "", target: "—" };
          const terminal = p === "messagerie";
          return (
            <div key={p} data-testid={`prio-row-${p}`}
              className={cn(TR, "px-5 py-3.5 grid gap-3 items-center grid-cols-[34px_minmax(0,1fr)] lg:grid-cols-[34px_minmax(180px,1.4fr)_minmax(160px,1.2fr)_96px_96px]")}>
              <div className="font-mono text-xs text-slate-300">{String(i + 1).padStart(2, "0")}</div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{meta.name}</div>
                <div className="text-xs text-slate-400 mt-0.5 text-pretty">{meta.condition}</div>
              </div>
              <div className="col-start-2 lg:col-start-auto flex items-center gap-2 min-w-0">
                <ArrowRight size={14} className="shrink-0 text-slate-300" />
                <span className="text-[13.5px] text-slate-600 truncate">{meta.target}</span>
              </div>
              <div className="col-start-2 lg:col-start-auto font-mono text-[12.5px] text-slate-400 lg:text-right">
                {terminal ? "—" : `${settings.ring_seconds} s`}
              </div>
              <div className="col-start-2 lg:col-start-auto flex items-center gap-1 lg:justify-end">
                {!terminal && (
                  <>
                    <button data-testid={`prio-up-${p}`} onClick={() => move(i, -1)} aria-label="Monter la règle" className={iconBtn}>
                      <ArrowUp size={14} />
                    </button>
                    <button data-testid={`prio-down-${p}`} onClick={() => move(i, 1)} aria-label="Descendre la règle" className={iconBtn}>
                      <ArrowDown size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Section>

      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(320px,1fr))] items-start">
        <Section title="Sonnerie, tentatives et rôles clés">
          <div className="p-5 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
            <label className={LABEL}>Temps de sonnerie (secondes)
              <input data-testid="setting-ring-seconds" type="number" min="5" max="120" className={cn(inputCls, "mt-1 font-mono")}
                value={settings.ring_seconds} onChange={(e) => set("ring_seconds", parseInt(e.target.value) || 20)} />
            </label>
            <label className={LABEL}>Tentatives max avant messagerie
              <input data-testid="setting-max-attempts" type="number" min="1" max="10" className={cn(inputCls, "mt-1 font-mono")}
                value={settings.max_attempts} onChange={(e) => set("max_attempts", parseInt(e.target.value) || 4)} />
            </label>
            <label className={LABEL}>Compte à rebours popup (secondes)
              <input data-testid="setting-popup-countdown" type="number" min="5" max="120" className={cn(inputCls, "mt-1 font-mono")}
                value={settings.popup_countdown_seconds || 20}
                onChange={(e) => set("popup_countdown_seconds", parseInt(e.target.value) || 20)} />
            </label>
            <label className={LABEL}>Commercial de remplacement
              <select data-testid="setting-replacement" className={cn(inputCls, "mt-1")} value={settings.replacement_commercial_id || ""}
                onChange={(e) => set("replacement_commercial_id", e.target.value || null)}>
                <option value="">— Aucun —</option>
                {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className={LABEL}>Manager de routage
              <select data-testid="setting-manager" className={cn(inputCls, "mt-1")} value={settings.manager_id || ""}
                onChange={(e) => set("manager_id", e.target.value || null)}>
                <option value="">— Premier manager disponible —</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
          </div>
        </Section>

        <Section title="Comportements">
          <div className="p-5 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
            <label className={LABEL}>Numéro inconnu
              <select data-testid="setting-behavior-unknown" className={cn(inputCls, "mt-1")} value={settings.behavior_unknown}
                onChange={(e) => set("behavior_unknown", e.target.value)}>
                <option value="round_robin">Round-robin commerciaux disponibles</option>
                <option value="general_number">Numéro général (1er numéro de secours)</option>
              </select>
            </label>
            <label className={LABEL}>Commercial responsable occupé
              <select data-testid="setting-busy-behavior" className={cn(inputCls, "mt-1")} value={settings.busy_behavior}
                onChange={(e) => set("busy_behavior", e.target.value)}>
                <option value="autre_commercial">Passer au suivant (autre commercial…)</option>
                <option value="attendre">Mettre en attente puis sonner</option>
              </select>
            </label>
            <label className={LABEL}>Numéro BLOQUÉ
              <select data-testid="setting-blocked-behavior" className={cn(inputCls, "mt-1")} value={settings.blocked_behavior}
                onChange={(e) => set("blocked_behavior", e.target.value)}>
                {BEHAVIORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className={LABEL}>Numéro INDÉSIRABLE
              <select data-testid="setting-indesirable-behavior" className={cn(inputCls, "mt-1")} value={settings.indesirable_behavior}
                onChange={(e) => set("indesirable_behavior", e.target.value)}>
                {BEHAVIORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {(settings.blocked_behavior === "numero_special" || settings.indesirable_behavior === "numero_special") && (
              <label className={cn(LABEL, "sm:col-span-2")}>Numéro spécial
                <input className={cn(inputCls, "mt-1 font-mono")} placeholder="+33 X XX XX XX XX" value={settings.blocked_special_number || ""}
                  onChange={(e) => set("blocked_special_number", e.target.value)} />
              </label>
            )}
            <label className={LABEL}>Seuil d'alerte (appels)
              <input data-testid="setting-anomaly-threshold" type="number" min="2" className={cn(inputCls, "mt-1 font-mono")} value={settings.anomaly_threshold}
                onChange={(e) => set("anomaly_threshold", parseInt(e.target.value) || 10)} />
            </label>
            <label className={LABEL}>Fenêtre d'alerte (minutes)
              <input type="number" min="10" className={cn(inputCls, "mt-1 font-mono")} value={settings.anomaly_window_minutes}
                onChange={(e) => set("anomaly_window_minutes", parseInt(e.target.value) || 120)} />
            </label>
          </div>
        </Section>

        <Section title="Horaires d'ouverture & hors horaires">
          <div className="p-5">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
              <label className={LABEL}>Début
                <input type="time" className={cn(inputCls, "mt-1 font-mono")} value={bh.start || "08:00"}
                  onChange={(e) => set("business_hours", { ...bh, start: e.target.value })} />
              </label>
              <label className={LABEL}>Fin
                <input type="time" className={cn(inputCls, "mt-1 font-mono")} value={bh.end || "20:00"}
                  onChange={(e) => set("business_hours", { ...bh, end: e.target.value })} />
              </label>
              <label className={LABEL}>Hors horaires
                <select className={cn(inputCls, "mt-1")} value={settings.off_hours_behavior}
                  onChange={(e) => set("off_hours_behavior", e.target.value)}>
                  <option value="messagerie">Messagerie</option>
                  <option value="secours">Numéros de secours puis messagerie</option>
                </select>
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3.5">
              {DAYS.map((d, i) => {
                const on = (bh.days || []).includes(i);
                return (
                  <button key={d} onClick={() => set("business_hours", { ...bh, days: on ? bh.days.filter((x) => x !== i) : [...(bh.days || []), i].sort() })}
                    aria-pressed={on}
                    className={cn("px-2.5 py-1 rounded-lg text-xs font-medium border",
                      on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300")}>
                    {d}
                  </button>
                );
              })}
            </div>

            {/* Annonce lue par la messagerie hors horaires. Elle vit ici, avec
                les horaires qu'elle énonce : un appelant qui tombe sur la
                messagerie à 21 h a besoin de savoir quand on décroche, pas
                d'entendre la même phrase qu'à 10 h. Facultative pour autant —
                une équipe peut préférer une annonce unique, ou ne pas vouloir
                dire ses horaires à tout appelant. */}
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="off-hours-announcement-toggle"
                checked={settings.off_hours_announcement !== false}
                onChange={(e) => set("off_hours_announcement", e.target.checked)} />
              Annonce spécifique hors horaires
            </label>

            {settings.off_hours_announcement === false ? (
              <p className="mt-1.5 text-xs text-slate-400 text-pretty">
                La messagerie lit son annonce habituelle à toute heure — celle réglée dans
                l'onglet <b className="font-medium text-slate-500">Fournisseur</b>. Le texte
                que vous aviez écrit est conservé si vous réactivez l'annonce.
              </p>
            ) : (
              <>
                <label className={cn(LABEL, "mt-2.5 block")}>
                  Ce qui est dit hors horaires
                  <textarea data-testid="off-hours-message" rows={3}
                    className={cn(inputCls, "mt-1 leading-relaxed")}
                    placeholder={settings.off_hours_message_preview || "Laisser vide pour l'annonce par défaut"}
                    value={settings.off_hours_message || ""}
                    onChange={(e) => set("off_hours_message", e.target.value)} />
                </label>
                <p className="mt-1.5 text-xs text-slate-400 text-pretty">
                  Écrivez <code className="font-mono text-slate-500">{"{horaires}"}</code> là où les
                  horaires doivent être dits : ils sont remplacés au moment de l'appel, donc
                  l'annonce reste juste si vous changez d'horaires. Laissez vide pour l'annonce
                  par défaut.
                </p>
                {settings.off_hours_message_preview && (
                  <p data-testid="off-hours-preview"
                    className="mt-2 rounded-xl border border-slate-100 bg-slate-50/70 p-3 text-[13px] italic leading-relaxed text-slate-600">
                    « {settings.off_hours_message_preview} »
                  </p>
                )}
              </>
            )}
          </div>
        </Section>

        <Section title="Numéros de secours" subtitle="Essayés dans l'ordre, avant la messagerie.">
          <div className="p-5 flex flex-col gap-2">
            {(settings.backup_numbers || []).map((b, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls} placeholder="Libellé (ex : Secours 1)" value={b.label || ""}
                  onChange={(e) => set("backup_numbers", settings.backup_numbers.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                <input data-testid={`backup-number-${i}`} className={cn(inputCls, "font-mono")} placeholder="+33 X XX XX XX XX" value={b.number || ""}
                  onChange={(e) => set("backup_numbers", settings.backup_numbers.map((x, j) => j === i ? { ...x, number: e.target.value } : x))} />
                <button onClick={() => set("backup_numbers", settings.backup_numbers.filter((_, j) => j !== i))}
                  aria-label="Supprimer ce numéro" className={cn(iconBtn, "shrink-0 hover:text-rose-600")}><Trash2 size={15} /></button>
              </div>
            ))}
            <button data-testid="add-backup-number-btn" onClick={() => set("backup_numbers", [...(settings.backup_numbers || []), { label: "", number: "" }])}
              className={cn(btnGhost, "self-start mt-1")}>
              <Plus size={14} /> Ajouter un numéro de secours
            </button>
          </div>
        </Section>
      </div>

      <div className="flex justify-end">
        <button data-testid="save-routing-settings-btn" onClick={save} className={btnPrimary}>
          <Save size={15} /> Enregistrer les paramètres
        </button>
      </div>

      {/* Groupes d'appel */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
        <div className="min-w-0">
          <h2 className="font-heading font-bold text-lg text-slate-900">Groupes d'appel</h2>
          <p className="text-xs text-slate-400 mt-1 text-pretty">
            Un appel dont le NAF ou le département correspond au groupe sonne en priorité ses membres.
          </p>
        </div>
        {!groupForm && (
          <button data-testid="add-group-btn" onClick={() => setGroupForm({ name: "", member_ids: [], naf_text: "", dept_text: "" })} className={btnGhost}>
            <Plus size={15} /> Nouveau groupe
          </button>
        )}
      </div>

      {groupForm && (
        <div className={cn(CARD, "p-4 flex flex-col gap-3")} data-testid="group-form">
          <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
            <input data-testid="group-name-input" className={inputCls} placeholder="Nom (ex : RESTAU)" value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
            <input data-testid="group-naf-input" className={cn(inputCls, "font-mono")} placeholder="Préfixes NAF (ex : 56, 5610)" value={groupForm.naf_text}
              onChange={(e) => setGroupForm({ ...groupForm, naf_text: e.target.value })} />
            <input data-testid="group-dept-input" className={cn(inputCls, "font-mono")} placeholder="Départements (ex : 38, 69, 01)" value={groupForm.dept_text}
              onChange={(e) => setGroupForm({ ...groupForm, dept_text: e.target.value })} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {commercials.map((c) => {
              const on = groupForm.member_ids.includes(c.id);
              return (
                <button key={c.id} data-testid={`group-member-${c.id}`} aria-pressed={on}
                  onClick={() => setGroupForm({ ...groupForm, member_ids: on ? groupForm.member_ids.filter((x) => x !== c.id) : [...groupForm.member_ids, c.id] })}
                  className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border",
                    on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")}>
                  {c.name}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button data-testid="group-save-btn" onClick={saveGroup} className={btnPrimary}><Save size={14} /> Enregistrer le groupe</button>
            <button onClick={() => setGroupForm(null)} className={btnGhost}>Annuler</button>
          </div>
        </div>
      )}

      <Section className="overflow-hidden">
        <div className={cn("hidden lg:grid px-5 py-2.5", THEAD)}
          style={{ gridTemplateColumns: "minmax(170px,1.2fr) 132px minmax(180px,1.4fr) 106px 130px" }}>
          <div>Groupe</div>
          <div>Secteurs NAF</div>
          <div>Membres</div>
          <div>Zones</div>
          <div className="text-right">Actions</div>
        </div>
        {groups.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-400">Aucun groupe — tous les commerciaux disponibles sont éligibles.</p>
        ) : groups.map((g) => (
          <div key={g.id} data-testid={`group-row-${g.name}`}
            className={cn(TR, "px-5 py-3.5 grid gap-3 items-center grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(170px,1.2fr)_132px_minmax(180px,1.4fr)_106px_130px]")}>
            <div className="text-sm font-medium text-slate-800 truncate">{g.name}</div>
            <div className="col-start-1 lg:col-start-auto font-mono text-[12.5px] text-slate-400 truncate">
              {g.naf_prefixes?.length ? g.naf_prefixes.join(", ") : "—"}
            </div>
            <div className="col-start-1 lg:col-start-auto flex flex-wrap items-center gap-1.5 min-w-0">
              {(g.member_ids || []).slice(0, 5).map((id, i) => (
                <span key={id} title={nameOf(id)}
                  className={cn("h-6 w-6 shrink-0 rounded-full text-white flex items-center justify-center text-[11px] font-semibold", AVATAR_BG[i % AVATAR_BG.length])}>
                  {nameOf(id)[0]}
                </span>
              ))}
              <span className="text-xs text-slate-400 ml-0.5">{(g.member_ids || []).length} membres</span>
            </div>
            <div className="col-start-1 lg:col-start-auto">
              <span className={cn(PILL, "bg-slate-100 text-slate-600")}>
                {g.departements?.length ? g.departements.join(", ") : "Toutes zones"}
              </span>
            </div>
            <div className="row-start-1 col-start-2 lg:row-start-auto lg:col-start-auto flex items-center justify-end gap-1.5">
              <button onClick={() => editGroup(g)} className={btnGhost} title={`Modifier ${g.name}`}>
                <Pencil size={14} /> <span className="hidden lg:inline">Modifier</span>
              </button>
              <button onClick={() => deleteGroup(g)} aria-label={`Supprimer ${g.name}`}
                className={cn(iconBtn, "border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700")}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}
